/** Wait without keeping a departed screen alive; reconnect wakes the retry early. */
export function waitForFlightRetry(delay, signal) {
  return new Promise((resolve, reject) => {
    const finish = () => { cleanup(); resolve(); };
    const abort = () => { cleanup(); reject(signal.reason || new DOMException("Aborted", "AbortError")); };
    const timer = setTimeout(finish, delay);
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      globalThis.window?.removeEventListener("online", finish);
    };
    signal?.addEventListener("abort", abort, { once: true });
    globalThis.window?.addEventListener("online", finish, { once: true });
    if (signal?.aborted) abort();
  });
}

/** Retry the same idempotent command, never turn an uncertain response into a new action. */
export async function syncFlightRequest(request, { signal, onRetry = () => {}, wait = waitForFlightRetry } = {}) {
  let retries = 0;
  while (true) {
    if (signal?.aborted) throw signal.reason || new DOMException("Aborted", "AbortError");
    try { return await request(); }
    catch (error) {
      if (signal?.aborted) throw signal.reason;
      if (error.retryable === false || ["migration_required", "server_configuration"].includes(error.code)) throw error;
      if (!(error.retryable || error.status === 429 || error.status >= 500 || error.code === "future_ticks")) throw error;
      onRetry(error);
      await wait(Math.min(8000, 1000 * 2 ** Math.min(retries++, 3)), signal);
    }
  }
}
